import {
  MAC88_LOCAL_IMAGE_BASE,
  MAC88_LOCAL_IMAGE_BY_CODE,
} from '../constants/mac88LocalImages'

/** Normalize for fuzzy name match: letters/digits, plurals stripped, tokens sorted. */
function nameMatchKey(s: string): string {
  const tokens = (s.toLowerCase().match(/[a-z]+|\d+/g) || []).map((t) => {
    if (t.length > 3 && t.endsWith('s')) return t.slice(0, -1)
    return t
  })
  return tokens.sort().join('')
}

/** Filename slug (without code prefix / extension) -> game code (exact token-key) */
const MAC88_NAME_TO_CODE: Record<string, string> = (() => {
  const out: Record<string, string> = {}
  for (const [code, file] of Object.entries(MAC88_LOCAL_IMAGE_BY_CODE)) {
    const base = file.replace(/^\d+_*/, '').replace(/\.[^.]+$/i, '')
    const key = nameMatchKey(base)
    if (key && !out[key]) out[key] = code
  }
  return out
})()

function codeFromBlob(raw: unknown): string | null {
  if (raw == null || raw === '') return null
  const s = String(raw).trim()
  if (/^\d{5,6}$/.test(s) && MAC88_LOCAL_IMAGE_BY_CODE[s]) return s
  // Prefer 15xxxx Mac88 table codes anywhere in the string / URL
  const all = s.match(/15\d{4}/g)
  if (all) {
    for (const c of all) {
      if (MAC88_LOCAL_IMAGE_BY_CODE[c]) return c
    }
  }
  // Any 5–6 digit run that exists in our local map
  const digits = s.match(/\d{5,6}/g)
  if (digits) {
    for (const c of digits) {
      if (MAC88_LOCAL_IMAGE_BY_CODE[c]) return c
    }
  }
  return null
}

function codeFromGameName(game: Record<string, unknown>): string | null {
  const name = String(game.game_name ?? game.name ?? '').trim()
  if (!name) return null
  const key = nameMatchKey(name)
  if (!key || key.length < 4) return null
  return MAC88_NAME_TO_CODE[key] ?? null
}

/**
 * DB often stores game_image as `/imgs/mac88/{code}_{name}.png` while
 * game_identifier is a hash (no code). Prefer that path when present.
 */
function localPathFromGameImage(game: Record<string, unknown>): string | null {
  for (const raw of [game.game_images, game.image_url, game.game_image]) {
    if (typeof raw !== 'string' || !raw.includes('/imgs/mac88/')) continue
    const file = raw.split('/imgs/mac88/')[1]?.split(/[?#]/)[0]
    if (!file || file.includes('placeholder')) continue
    try {
      return `${MAC88_LOCAL_IMAGE_BASE}/${encodeURI(decodeURIComponent(file))}`
    } catch {
      return `${MAC88_LOCAL_IMAGE_BASE}/${encodeURI(file)}`
    }
  }
  return null
}

/** Pull Mac88 numeric game code from API game fields / image URL / name. */
export function extractMac88GameCode(game: Record<string, unknown>): string | null {
  const candidates = [
    game.game_code,
    game.gameCode,
    game.identifier,
    game.game_identifier,
    game.game_id,
    game._id,
    game.game_images,
    game.image_url,
    game.game_image,
  ]
  for (const raw of candidates) {
    const code = codeFromBlob(raw)
    if (code) return code
  }
  return codeFromGameName(game)
}

export function isMac88ProviderGame(game: Record<string, unknown>): boolean {
  const prov = String(
    game._providerCategory ?? game.provider ?? game.provider_name ?? game.game_provider ?? '',
  ).toLowerCase()
  if (prov.includes('mac88')) return true
  return extractMac88GameCode(game) != null
}

/**
 * Local thumbnail under public/imgs/mac88.
 * encodeURI keeps path structure and encodes spaces for Linux/nginx.
 */
export function resolveMac88LocalImageUrl(game: Record<string, unknown>): string | null {
  // 1) Trust DB path when it already points at public/imgs/mac88
  const fromDb = localPathFromGameImage(game)
  if (fromDb) return fromDb

  // 2) Map by numeric code / name when identifier is a hash
  const code = extractMac88GameCode(game)
  if (!code) return null
  const file = MAC88_LOCAL_IMAGE_BY_CODE[code]
  if (!file) return null
  return `${MAC88_LOCAL_IMAGE_BASE}/${encodeURI(file)}`
}

/** Remote CDN / API image URL for fallback when local file 404s. */
export function resolveMac88RemoteImageUrl(game: Record<string, unknown>): string {
  const url = game.game_images || game.image_url || game.game_image
  return typeof url === 'string' ? url.trim() : ''
}
